import { Badge } from "@/components/ui/badge";
import { useCurrentClient } from "@/features/clients/hooks/use-clients";
import { useMessages } from "@/features/messages/hooks/use-messages";
import { relatedMessages, type ClientMessage } from "@/features/messages/types";
import { useDateFormat, useTranslation } from "@/features/utils/i18n";
import { Link } from "react-router-dom";
import { messageStatusKey } from "./message-list-item";

/**
 * The same contact's other requests: those still open, and those closed lately.
 * The review pass is meant to add to a request rather than open another, and when it
 * does not, this is where the team sees the duplicate and can close one of the two.
 */
export function RelatedMessages({ message }: { message: ClientMessage }) {
  const { t } = useTranslation("messages");
  const { format } = useDateFormat();
  const clientId = useCurrentClient()?.client?.id || "";
  const { data } = useMessages(
    { customerId: message.customer_id },
    { enabled: !!message.customer_id },
  );

  if (!message.customer_id) return null;
  const { open, closed } = relatedMessages(message, data?.messages || []);
  if (!open.length && !closed.length) return null;

  const row = (m: ClientMessage) => (
    <li key={m.id}>
      <Link
        to={`/${clientId}/messages/${m.id}`}
        className="flex items-center justify-between gap-3 rounded-md border p-2 text-sm hover:bg-muted/50"
      >
        <span className="min-w-0 truncate font-medium">{m.subject}</span>
        <span className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
          {m.status === "handled" && m.handled_at
            ? t("view.relatedClosedAt", { date: format(m.handled_at, "P") })
            : format(m.created_at, "P")}
          <Badge variant={m.status === "pending" ? "outline" : "secondary"}>
            {t(`status.${messageStatusKey(m)}`)}
          </Badge>
        </span>
      </Link>
    </li>
  );

  return (
    <section className="space-y-2">
      <h3 className="text-sm font-medium text-muted-foreground">
        {t("view.related")}
      </h3>
      {open.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">{t("view.relatedOpen")}</p>
          <ul className="space-y-1">{open.map(row)}</ul>
        </div>
      )}
      {closed.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">
            {t("view.relatedClosed")}
          </p>
          <ul className="space-y-1">{closed.map(row)}</ul>
        </div>
      )}
    </section>
  );
}
